/*
 * geo-currency.js — Geolocation-based DISPLAY currency conversion for Leya
 * -----------------------------------------------------------------------------
 * Store base currency: INR. This script detects a visitor's country by IP and
 * rewrites the *displayed* prices site-wide into a target currency using live
 * exchange rates (with a hardcoded fallback).
 *
 * IMPORTANT — display only:
 *   Shopify checkout still charges in the store's base currency (INR) because
 *   this store uses a third-party gateway (no Shopify Payments in India). This
 *   script changes what shoppers SEE, not what the gateway charges.
 *
 * Each visitor sees their own country's native currency (e.g. Japan -> JPY,
 * Canada -> CAD, Switzerland -> CHF), via a full ISO country -> currency map.
 * Countries that don't map to a currency we can convert to (unmapped, or no
 * exchange rate available) fall back to displaying the store's own INR price.
 *
 * Currency is chosen in this precedence order:
 *   1. Test override         (?leya_currency=XXX / LeyaGeo.set)
 *   2. Explicit selection    (shopper picked a country in Shopify's selector)
 *   3. Geo-IP detection      (cached in sessionStorage for 30 min, then fresh)
 *   4. INR fallback
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CONFIG = {
    baseCurrency: 'INR',
    // The detected country is cached per-tab in sessionStorage for a short
    // time (geoTtl) so navigating between pages doesn't repeat the ~0.75s
    // ipwho.is lookup. A VPN switch therefore shows after geoTtl or in a new
    // tab/session, not on the very next page load. FX rates are cached longer
    // (rateTtl), since they don't change when a visitor changes location.
    geoTtl: 30 * 60 * 1000, // 30 min
    rateTtl: 12 * 60 * 60 * 1000, // 12h
    // Round converted foreign amounts to whole units (0 = no decimals).
    fractionDigits: 0,
    // Containers whose text nodes may contain prices. Scoped to avoid touching
    // product descriptions or other prose that might mention a number.
    priceSelectors: [
      '.price',
      '.product__price',
      '.unit-price',
      '.cart-item__price-wrapper',
      '.cart-item__totals',
      '.cart-item__price',
      '.totals',
      '.cart__footer',
      '.drawer__footer',
      '.cart-drawer__footer',
      '[data-cart-subtotal]',
      '.product-price',
      '.product-option',
      '.money'
    ]
  };

  // ---- Country -> native currency map ----------------------------------------
  // ISO 3166-1 alpha-2 country code -> ISO 4217 currency code. Built from small
  // per-currency groups (eurozone, USD-pegged/dollarized territories, etc.) plus
  // each remaining country's own currency, so it's easy to audit and extend.
  var CURRENCY_GROUPS = {
    EUR: [
      'AD', 'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE',
      'IT', 'LT', 'LU', 'LV', 'MC', 'ME', 'MT', 'NL', 'PT', 'SI', 'SK', 'SM',
      'VA', 'XK'
    ],
    USD: [
      'US', 'EC', 'SV', 'TL', 'PR', 'VI', 'GU', 'AS', 'MP', 'PW', 'FM', 'MH',
      'TC', 'ZW', 'PA'
    ],
    GBP: ['GB', 'GG', 'IM', 'JE'],
    XCD: ['AG', 'DM', 'GD', 'KN', 'LC', 'VC', 'AI', 'MS'],
    XOF: ['BJ', 'BF', 'CI', 'GW', 'ML', 'NE', 'SN', 'TG'],
    XAF: ['CM', 'CF', 'TD', 'CG', 'GQ', 'GA'],
    CHF: ['CH', 'LI']
  };

  var COUNTRY_CURRENCY = {
    // Americas
    CA: 'CAD', MX: 'MXN', GT: 'GTQ', BZ: 'BZD', HN: 'HNL', NI: 'NIO',
    CR: 'CRC', CU: 'CUP', JM: 'JMD', HT: 'HTG', DO: 'DOP', TT: 'TTD',
    BB: 'BBD', BS: 'BSD', BM: 'BMD', GY: 'GYD', SR: 'SRD', BR: 'BRL',
    AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', VE: 'VES', BO: 'BOB',
    PY: 'PYG', UY: 'UYU', KY: 'KYD', AW: 'AWG', CW: 'ANG',
    // Europe (non-eurozone)
    NO: 'NOK', SE: 'SEK', DK: 'DKK', IS: 'ISK', PL: 'PLN', CZ: 'CZK',
    HU: 'HUF', RO: 'RON', BG: 'BGN', RS: 'RSD', BA: 'BAM', MK: 'MKD',
    AL: 'ALL', MD: 'MDL', UA: 'UAH', BY: 'BYN', RU: 'RUB', GE: 'GEL',
    AM: 'AMD', AZ: 'AZN',
    // Middle East
    AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR',
    JO: 'JOD', LB: 'LBP', IL: 'ILS', PS: 'ILS', IQ: 'IQD', IR: 'IRR',
    YE: 'YER', SY: 'SYP', TR: 'TRY',
    // Asia
    IN: 'INR', PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR', BT: 'BTN',
    MV: 'MVR', AF: 'AFN', CN: 'CNY', JP: 'JPY', KR: 'KRW', KP: 'KPW',
    MN: 'MNT', HK: 'HKD', MO: 'MOP', TW: 'TWD', TH: 'THB', VN: 'VND',
    LA: 'LAK', KH: 'KHR', MM: 'MMK', MY: 'MYR', SG: 'SGD', ID: 'IDR',
    PH: 'PHP', BN: 'BND', KZ: 'KZT', UZ: 'UZS', TM: 'TMT', TJ: 'TJS',
    KG: 'KGS',
    // Oceania
    AU: 'AUD', NZ: 'NZD', FJ: 'FJD', PG: 'PGK', SB: 'SBD', VU: 'VUV',
    WS: 'WST', TO: 'TOP', KI: 'AUD', TV: 'AUD', NR: 'AUD',
    // Africa
    DZ: 'DZD', AO: 'AOA', BJ: 'XOF', BW: 'BWP', BF: 'XOF', BI: 'BIF',
    CV: 'CVE', CM: 'XAF', CF: 'XAF', TD: 'XAF', KM: 'KMF', CG: 'XAF',
    CD: 'CDF', CI: 'XOF', DJ: 'DJF', EG: 'EGP', GQ: 'XAF', ER: 'ERN',
    SZ: 'SZL', ET: 'ETB', GA: 'XAF', GM: 'GMD', GH: 'GHS', GN: 'GNF',
    GW: 'XOF', KE: 'KES', LS: 'LSL', LR: 'LRD', LY: 'LYD', MG: 'MGA',
    MW: 'MWK', ML: 'XOF', MR: 'MRU', MU: 'MUR', MA: 'MAD', MZ: 'MZN',
    NA: 'NAD', NE: 'XOF', NG: 'NGN', RW: 'RWF', ST: 'STN', SN: 'XOF',
    SC: 'SCR', SL: 'SLE', SO: 'SOS', ZA: 'ZAR', SS: 'SSP', SD: 'SDG',
    TZ: 'TZS', TG: 'XOF', TN: 'TND', UG: 'UGX', ZM: 'ZMW'
  };
  (function expandGroups() {
    Object.keys(CURRENCY_GROUPS).forEach(function (currency) {
      CURRENCY_GROUPS[currency].forEach(function (cc) {
        COUNTRY_CURRENCY[cc] = currency;
      });
    });
  })();

  function resolveCurrency(countryCode) {
    var cc = (countryCode || '').toUpperCase();
    return COUNTRY_CURRENCY[cc] || CONFIG.baseCurrency; // unmapped -> INR (base)
  }

  // ---- Small localStorage cache helpers -------------------------------------
  function cacheGet(key, ttl) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || typeof obj.ts !== 'number') return null;
      if (Date.now() - obj.ts > ttl) return null;
      return obj.data;
    } catch (e) {
      return null;
    }
  }
  function cacheSet(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data: data }));
    } catch (e) {
      /* storage full / disabled — ignore */
    }
  }
  // Like cacheGet but ignores TTL — used to read the last successfully
  // fetched rates, however old, as a fallback when the live API is down.
  function cacheGetAny(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      return obj && obj.data ? obj.data : null;
    } catch (e) {
      return null;
    }
  }

  // ---- Geo detection (IP based) ---------------------------------------------
  // Any 3-letter ISO 4217 code is accepted (e.g. JPY, CAD, CHF) — validity of
  // the actual conversion is checked later, when a rate is looked up.
  var ISO_CURRENCY_RE = /^[A-Z]{3}$/;

  // Testing overrides, checked BEFORE any IP lookup or cache:
  //   1. URL param:    ?leya_currency=JPY
  //   2. localStorage: localStorage.setItem('leya_currency_override','JPY')
  //      (or use the console helper LeyaGeo.set('JPY'))
  function overrideCurrency() {
    try {
      var param = new URLSearchParams(window.location.search).get('leya_currency');
      if (param && ISO_CURRENCY_RE.test(param.toUpperCase())) {
        return param.toUpperCase();
      }
      var stored = localStorage.getItem('leya_currency_override');
      if (stored && ISO_CURRENCY_RE.test(stored.toUpperCase())) {
        return stored.toUpperCase();
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  // If the shopper has explicitly picked a country in Shopify's country
  // selector, that choice wins over IP detection. We record the choice (see
  // watchCountrySelector) and read the active country Shopify injected inline.
  function selectorCurrency() {
    try {
      if (localStorage.getItem('leya_country_selected') !== '1') return null;
      var country = window.LeyaShopify && window.LeyaShopify.country;
      if (!country) return null;
      return {
        currency: resolveCurrency(country),
        country: country.toUpperCase(),
        source: 'selector'
      };
    } catch (e) {
      return null;
    }
  }

  // Per-visit geo cache (sessionStorage). Only successful lookups that returned
  // a country are stored, so a failed lookup is retried on the next page.
  var GEO_KEY = 'leya_geo_session';
  function geoCacheGet() {
    try {
      var obj = JSON.parse(sessionStorage.getItem(GEO_KEY));
      if (!obj || typeof obj.ts !== 'number' || !obj.country) return null;
      if (Date.now() - obj.ts > CONFIG.geoTtl) return null;
      return { country: obj.country, source: obj.source || 'geo' };
    } catch (e) {
      return null;
    }
  }
  function geoCacheSet(country, source) {
    if (!country) return;
    try {
      sessionStorage.setItem(
        GEO_KEY,
        JSON.stringify({ ts: Date.now(), country: country, source: source })
      );
    } catch (e) {
      /* storage disabled — ignore */
    }
  }

  function detectCurrency() {
    var forced = overrideCurrency();
    if (forced) {
      return Promise.resolve({ currency: forced, country: null, source: 'override' });
    }

    var selected = selectorCurrency();
    if (selected) {
      return Promise.resolve(selected);
    }

    // Reuse this visit's detected country (sessionStorage, short expiry) instead
    // of calling the IP API on every page. Trade-off: a VPN switch is picked up
    // after geoTtl (or in a new tab) rather than on the very next page load.
    var cachedGeo = geoCacheGet();
    if (cachedGeo) {
      return Promise.resolve({
        currency: resolveCurrency(cachedGeo.country),
        country: cachedGeo.country,
        source: cachedGeo.source + '-cached'
      });
    }

    function fromResponse(d, source) {
      geoCacheSet(d.country_code, source);
      return {
        currency: resolveCurrency(d.country_code),
        country: d.country_code || null,
        source: source
      };
    }

    return fetch('https://ipwho.is/?fields=country_code,success')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || d.success === false) throw new Error('geo lookup failed');
        return fromResponse(d, 'ipwho.is');
      })
      .catch(function () {
        // Fallback provider
        return fetch('https://ipapi.co/json/')
          .then(function (r) { return r.json(); })
          .then(function (d) { return fromResponse(d, 'ipapi.co'); })
          .catch(function () {
            return { currency: 'INR', country: null, source: 'fallback' };
          });
      });
  }

  // ---- Exchange rate (INR -> target) ----------------------------------------
  // Returns a Promise<number|null>. null means "no usable rate" — the caller
  // falls back to showing the store's own INR price rather than guessing.
  //
  // There is no hardcoded fallback rate table: every time we successfully
  // fetch live rates, the full rate set is also written to a non-expiring
  // "last known good" cache (leya_fx_rates_last). If the live API is down and
  // the short-lived (12h) cache has also expired, we fall back to whatever
  // rates were last successfully recorded for that specific currency — old
  // data is far more accurate than a hardcoded guess, and it self-updates
  // every time the API is reachable again.
  function getRate(target) {
    if (target === 'INR') return Promise.resolve(1);

    var cached = cacheGet('leya_fx_rates', CONFIG.rateTtl);
    if (cached && typeof cached[target] === 'number') {
      return Promise.resolve(cached[target]);
    }

    return fetch('https://open.er-api.com/v6/latest/INR')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.rates) throw new Error('rate lookup failed');
        cacheSet('leya_fx_rates', d.rates);
        cacheSet('leya_fx_rates_last', d.rates); // non-expiring backup
        if (typeof d.rates[target] === 'number') return d.rates[target];
        var lastKnown = cacheGetAny('leya_fx_rates_last');
        return lastKnown && typeof lastKnown[target] === 'number' ? lastKnown[target] : null;
      })
      .catch(function () {
        var lastKnown = cacheGetAny('leya_fx_rates_last');
        return lastKnown && typeof lastKnown[target] === 'number' ? lastKnown[target] : null;
      });
  }

  // ---- Price rewriting ------------------------------------------------------
  // Match INR money tokens like: ₹16,600.00 / Rs. 16,600 / ₹16,600.00 INR
  // Only INR tokens are matched, so the pass is naturally idempotent: once a
  // token is converted to $/£/€ etc. it will not be matched again.
  var INR_MONEY_RE = /(?:₹|Rs\.?|INR)\s?(\d[\d,]*(?:\.\d{1,2})?)(?:\s?INR)?/gi;

  function makeFormatter(currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: CONFIG.fractionDigits,
        maximumFractionDigits: CONFIG.fractionDigits
      });
    } catch (e) {
      return null;
    }
  }

  function convertTextNode(node, rate, formatter) {
    var text = node.nodeValue;
    if (text.indexOf('₹') === -1 && !/Rs|INR/i.test(text)) return;
    var replaced = text.replace(INR_MONEY_RE, function (_match, num) {
      var amount = parseFloat(num.replace(/,/g, ''));
      if (isNaN(amount)) return _match;
      var converted = amount * rate;
      return formatter ? formatter.format(converted) : String(Math.round(converted));
    });
    if (replaced !== text) node.nodeValue = replaced;
  }

  function walkAndConvert(root, rate, formatter) {
    var containers = [];
    CONFIG.priceSelectors.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) { containers.push(el); });
    });
    if (root.matches) {
      CONFIG.priceSelectors.forEach(function (sel) {
        if (root.matches(sel)) containers.push(root);
      });
    }
    containers.forEach(function (container) {
      var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
      var textNodes = [];
      var n;
      while ((n = walker.nextNode())) textNodes.push(n);
      textNodes.forEach(function (tn) { convertTextNode(tn, rate, formatter); });
    });
  }

  // ---- Country selector watcher ---------------------------------------------
  // Dawn's country selector (<localization-form>) sets a hidden country_code
  // input and submits the form, reloading the page with the new country active.
  // We flag that the shopper made an explicit choice so it wins over IP on the
  // reloaded page (and every page after).
  function watchCountrySelector() {
    function mark() {
      try { localStorage.setItem('leya_country_selected', '1'); } catch (e) {}
    }
    function bind() {
      document.querySelectorAll('localization-form form, form#localization_form').forEach(
        function (form) {
          if (form.dataset.leyaBound) return;
          form.dataset.leyaBound = '1';
          form.addEventListener('submit', mark, true);
        }
      );
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind);
    } else {
      bind();
    }
  }

  // ---- Debug / testing helpers ----------------------------------------------
  function installHelpers(info, rate) {
    if (info.source === 'override') {
      console.warn(
        '[Leya] Currency OVERRIDE active: ' + info.currency +
        '. This ignores your real location. Run LeyaGeo.reset() to use geo-IP detection.'
      );
    } else {
      console.info('[Leya] Geo currency detected:', info, 'rate(INR->' + info.currency + ')=' + rate);
    }
    window.LeyaGeo = {
      info: info, // { currency, country, source }
      rate: rate,
      // Force a currency for testing, e.g. LeyaGeo.set('EUR'); reloads the page.
      set: function (code) {
        try {
          localStorage.setItem('leya_currency_override', String(code).toUpperCase());
        } catch (e) {}
        location.reload();
      },
      // Clear override + explicit country selection + cached geo so real IP
      // detection runs again.
      reset: function () {
        try {
          localStorage.removeItem('leya_currency_override');
          localStorage.removeItem('leya_country_selected');
          localStorage.removeItem('leya_geo');
          localStorage.removeItem('leya_geo_currency'); // legacy key
          sessionStorage.removeItem(GEO_KEY);
        } catch (e) {}
        location.reload();
      }
    };
  }

  // ---- Boot -----------------------------------------------------------------
  watchCountrySelector();

  detectCurrency().then(function (info) {
    if (info.currency === CONFIG.baseCurrency) {
      installHelpers(info, 1); // rest of world: leave INR as-is
      return;
    }

    getRate(info.currency).then(function (rate) {
      // No usable rate (live API down + nothing recorded before, or an
      // override typo'd a currency we can't format/rate) — show the store's
      // own INR price rather than a broken/guessed conversion.
      if (rate === null) {
        installHelpers(
          { currency: CONFIG.baseCurrency, country: info.country, source: info.source + '-no-rate' },
          1
        );
        return;
      }

      var formatter = makeFormatter(info.currency);
      if (!formatter) {
        installHelpers(
          { currency: CONFIG.baseCurrency, country: info.country, source: info.source + '-bad-currency' },
          1
        );
        return;
      }

      function runAll() { walkAndConvert(document.body, rate, formatter); }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', runAll);
      } else {
        runAll();
      }

      // Re-convert dynamically injected/updated prices (cart drawer, variant
      // change, quick-add, pagination, etc.). Every mutation batch adds its
      // nodes to a queue; ONE requestAnimationFrame flush walks them all, so
      // batches that arrive while a flush is pending are not dropped.
      var queue = new Set();
      var pending = false;
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType === 1) queue.add(node);
          });
          if (m.type === 'characterData' && m.target && m.target.parentNode) {
            queue.add(m.target.parentNode);
          }
        });
        if (pending || !queue.size) return;
        pending = true;
        window.requestAnimationFrame(function () {
          var nodes = Array.from(queue);
          queue.clear();
          pending = false;
          nodes.forEach(function (node) {
            if (node.isConnected) walkAndConvert(node, rate, formatter);
          });
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true
      });

      // Expose resolved geo info + testing helpers for debugging.
      installHelpers(info, rate);
    });
  });
})();


// =============================================================================
// STAGING-ONLY VERSION — COMMENTED OUT so staging matches live.
// IP detection on every page load + resync of Shopify's own market/country.
// To re-enable: replace everything above this banner with the code below
// (remove the leading "// " from each line), and swap the window.LeyaShopify
// block in layout/theme.liquid for the commented window.ShopifyLocalization one.
// =============================================================================
// /*
//  * geo-currency.js — Display currency + market resync for Leya
//  * -----------------------------------------------------------------------------
//  * Shopify Markets has a genuine +10% price list on every market except India
//  * (real unit-economics markup, applied at checkout). Which market a visitor
//  * is assigned to is controlled by Shopify's own native country selector,
//  * which sets a long-lived Shopify cookie that does NOT re-check IP on every
//  * visit (by design, so a cart's currency doesn't flip mid-checkout). That
//  * means a visitor who was ever assigned to e.g. the Australia/NZ market keeps
//  * being charged that market's marked-up price until they manually reselect a
//  * country — even after closing the browser, disconnecting a VPN, etc.
//  *
//  * Logic:
//  *   1. Look up the visitor's country from their IP (geo-IP API), independent
//  *      of any Shopify market/login/account state. No caching of any kind —
//  *      re-checked fresh on every single page load, so it's never stuck on a
//  *      stale location for any length of time.
//  *   2. If that detected country doesn't match Shopify's currently assigned
//  *      market/country (`window.ShopifyLocalization.country`, set server-side
//  *      in theme.liquid), resubmit Shopify's own native country-selector form
//  *      with the detected country — the exact same mechanism a shopper uses
//  *      manually. This corrects the *real* market/price-list assignment (not
//  *      just displayed text), so checkout always matches the visitor's actual
//  *      current location. The page reloads as part of that form submit.
//  *   3. Otherwise (already on the right market), convert displayed INR prices
//  *      to the visitor's local currency using a live FX rate, fetched fresh
//  *      every page load — no caching.
//  *   4. If ANY step fails or is unavailable (IP lookup fails, country not in
//  *      the table, no live FX rate, no selector form on the page) — just show
//  *      the store's own INR price / leave the market as Shopify has it. No
//  *      guessing, no manual override, nothing persisted anywhere.
//  * -----------------------------------------------------------------------------
//  */
// (function () {
//   'use strict';
//
//   var CONFIG = {
//     baseCurrency: 'INR',
//     // Free IP-geolocation lookup — returns the visitor's country, nothing more.
//     geoApiUrl: 'https://ipwho.is/',
//     // rateTtl: 12 * 60 * 60 * 1000, // 12h — was used to cache FX rates; caching removed, kept here for reference
//     // Round converted foreign amounts to whole units (0 = no decimals).
//     fractionDigits: 0,
//     // Containers whose text nodes may contain prices. Scoped to avoid touching
//     // product descriptions or other prose that might mention a number.
//     priceSelectors: [
//       '.price',
//       '.product__price',
//       '.unit-price',
//       '.cart-item__price-wrapper',
//       '.cart-item__totals',
//       '.cart-item__price',
//       '.totals',
//       '.cart__footer',
//       '.drawer__footer',
//       '.cart-drawer__footer',
//       '[data-cart-subtotal]',
//       '.product-price',
//       '.product-option',
//       '.money'
//     ],
//     // Country (ISO 3166-1 alpha-2) -> currency (ISO 4217). Standard official
//     // currency per country/territory — not tied to Shopify Markets. A country
//     // not listed, or a currency with no live FX rate available, falls back to
//     // the store default currency (INR).
//     countryCurrencyMap: {
//       AD: 'EUR', AE: 'AED', AF: 'AFN', AG: 'XCD', AI: 'XCD', AL: 'ALL', AM: 'AMD', AO: 'AOA',
//       AQ: 'USD', AR: 'ARS', AS: 'USD', AT: 'EUR', AU: 'AUD', AW: 'AWG', AX: 'EUR', AZ: 'AZN',
//       BA: 'BAM', BB: 'BBD', BD: 'BDT', BE: 'EUR', BF: 'XOF', BG: 'BGN', BH: 'BHD', BI: 'BIF',
//       BJ: 'XOF', BL: 'EUR', BM: 'BMD', BN: 'BND', BO: 'BOB', BQ: 'USD', BR: 'BRL', BS: 'BSD',
//       BT: 'BTN', BV: 'NOK', BW: 'BWP', BY: 'BYN', BZ: 'BZD',
//       CA: 'CAD', CC: 'AUD', CD: 'CDF', CF: 'XAF', CG: 'XAF', CH: 'CHF', CI: 'XOF', CK: 'NZD',
//       CL: 'CLP', CM: 'XAF', CN: 'CNY', CO: 'COP', CR: 'CRC', CU: 'CUP', CV: 'CVE', CW: 'ANG',
//       CX: 'AUD', CY: 'EUR', CZ: 'CZK',
//       DE: 'EUR', DJ: 'DJF', DK: 'DKK', DM: 'XCD', DO: 'DOP', DZ: 'DZD',
//       EC: 'USD', EE: 'EUR', EG: 'EGP', EH: 'MAD', ER: 'ERN', ES: 'EUR', ET: 'ETB',
//       FI: 'EUR', FJ: 'FJD', FK: 'FKP', FM: 'USD', FO: 'DKK', FR: 'EUR',
//       GA: 'XAF', GB: 'GBP', GD: 'XCD', GE: 'GEL', GF: 'EUR', GG: 'GBP', GH: 'GHS', GI: 'GIP',
//       GL: 'DKK', GM: 'GMD', GN: 'GNF', GP: 'EUR', GQ: 'XAF', GR: 'EUR', GS: 'GBP', GT: 'GTQ',
//       GU: 'USD', GW: 'XOF', GY: 'GYD',
//       HK: 'HKD', HM: 'AUD', HN: 'HNL', HR: 'EUR', HT: 'HTG', HU: 'HUF',
//       ID: 'IDR', IE: 'EUR', IL: 'ILS', IM: 'GBP', IN: 'INR', IO: 'USD', IQ: 'IQD', IR: 'IRR',
//       IS: 'ISK', IT: 'EUR',
//       JE: 'GBP', JM: 'JMD', JO: 'JOD', JP: 'JPY',
//       KE: 'KES', KG: 'KGS', KH: 'KHR', KI: 'AUD', KM: 'KMF', KN: 'XCD', KP: 'KPW', KR: 'KRW',
//       KW: 'KWD', KY: 'KYD', KZ: 'KZT',
//       LA: 'LAK', LB: 'LBP', LC: 'XCD', LI: 'CHF', LK: 'LKR', LR: 'LRD', LS: 'LSL', LT: 'EUR',
//       LU: 'EUR', LV: 'EUR', LY: 'LYD',
//       MA: 'MAD', MC: 'EUR', MD: 'MDL', ME: 'EUR', MF: 'EUR', MG: 'MGA', MH: 'USD', MK: 'MKD',
//       ML: 'XOF', MM: 'MMK', MN: 'MNT', MO: 'MOP', MP: 'USD', MQ: 'EUR', MR: 'MRU', MS: 'XCD',
//       MT: 'EUR', MU: 'MUR', MV: 'MVR', MW: 'MWK', MX: 'MXN', MY: 'MYR', MZ: 'MZN',
//       NA: 'NAD', NC: 'XPF', NE: 'XOF', NF: 'AUD', NG: 'NGN', NI: 'NIO', NL: 'EUR', NO: 'NOK',
//       NP: 'NPR', NR: 'AUD', NU: 'NZD', NZ: 'NZD',
//       OM: 'OMR',
//       PA: 'PAB', PE: 'PEN', PF: 'XPF', PG: 'PGK', PH: 'PHP', PK: 'PKR', PL: 'PLN', PM: 'EUR',
//       PN: 'NZD', PR: 'USD', PS: 'ILS', PT: 'EUR', PW: 'USD', PY: 'PYG',
//       QA: 'QAR',
//       RE: 'EUR', RO: 'RON', RS: 'RSD', RU: 'RUB', RW: 'RWF',
//       SA: 'SAR', SB: 'SBD', SC: 'SCR', SD: 'SDG', SE: 'SEK', SG: 'SGD', SH: 'SHP', SI: 'EUR',
//       SJ: 'NOK', SK: 'EUR', SL: 'SLE', SM: 'EUR', SN: 'XOF', SO: 'SOS', SR: 'SRD', SS: 'SSP',
//       ST: 'STN', SV: 'USD', SX: 'ANG', SY: 'SYP', SZ: 'SZL',
//       TC: 'USD', TD: 'XAF', TF: 'EUR', TG: 'XOF', TH: 'THB', TJ: 'TJS', TK: 'NZD', TL: 'USD',
//       TM: 'TMT', TN: 'TND', TO: 'TOP', TR: 'TRY', TT: 'TTD', TV: 'AUD', TW: 'TWD', TZ: 'TZS',
//       UA: 'UAH', UG: 'UGX', US: 'USD', UY: 'UYU', UZ: 'UZS',
//       VA: 'EUR', VC: 'XCD', VE: 'VES', VG: 'USD', VI: 'USD', VN: 'VND', VU: 'VUV',
//       WF: 'XPF', WS: 'WST',
//       YE: 'YER', YT: 'EUR',
//       ZA: 'ZAR', ZM: 'ZMW', ZW: 'ZWL'
//     }
//   };
//
//   // ---- Small localStorage cache helpers -------------------------------------
//   // NOTE: caching removed (country detection and FX rates are now fetched
//   // fresh on every page load) — helpers kept here, commented out, for
//   // reference rather than deleted outright.
//   // function cacheGet(key, ttl) {
//   //   try {
//   //     var raw = localStorage.getItem(key);
//   //     if (!raw) return null;
//   //     var obj = JSON.parse(raw);
//   //     if (!obj || typeof obj.ts !== 'number') return null;
//   //     if (Date.now() - obj.ts > ttl) return null;
//   //     return obj.data;
//   //   } catch (e) {
//   //     return null;
//   //   }
//   // }
//   // function cacheSet(key, data) {
//   //   try {
//   //     localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data: data }));
//   //   } catch (e) {
//   //     /* storage full / disabled — ignore */
//   //   }
//   // }
//   // function cacheGetAny(key) {
//   //   try {
//   //     var raw = localStorage.getItem(key);
//   //     if (!raw) return null;
//   //     var obj = JSON.parse(raw);
//   //     return obj && obj.data ? obj.data : null;
//   //   } catch (e) {
//   //     return null;
//   //   }
//   // }
//
//   // ---- Small sessionStorage helpers ------------------------------------------
//   // NOTE: caching removed (country detection is now fetched fresh on every
//   // page load, not once per session) — helpers kept here, commented out.
//   // function sessionGet(key) {
//   //   try {
//   //     var raw = sessionStorage.getItem(key);
//   //     return raw ? JSON.parse(raw) : null;
//   //   } catch (e) {
//   //     return null;
//   //   }
//   // }
//   // function sessionSet(key, data) {
//   //   try {
//   //     sessionStorage.setItem(key, JSON.stringify(data));
//   //   } catch (e) {
//   //     /* storage full / disabled — ignore */
//   //   }
//   // }
//
//   // ---- Visitor's country, via IP geolocation ---------------------------------
//   // Returns a Promise<string|null>. null means "couldn't detect" — caller
//   // falls back to the store's own INR price. No caching — fetched fresh on
//   // every page load.
//   function detectCountry() {
//     return fetch(CONFIG.geoApiUrl)
//       .then(function (r) { return r.json(); })
//       .then(function (d) {
//         if (!d || d.success === false || !d.country_code) return null;
//         return d.country_code;
//       })
//       .catch(function () {
//         return null;
//       });
//   }
//
//   function currencyForCountry(country) {
//     return (country && CONFIG.countryCurrencyMap[country]) || CONFIG.baseCurrency;
//   }
//
//   // ---- Resync Shopify's own market/country if stale --------------------------
//   // Returns true if a resync was triggered (page is about to reload via the
//   // native country-selector form's own submit) — caller should skip the
//   // display-conversion pass for this render, since a fresh one is coming.
//   function syncShopifyMarket(targetCountry) {
//     if (!targetCountry) return false;
//
//     var current = window.ShopifyLocalization && window.ShopifyLocalization.country;
//     if (!current || current === targetCountry) return false;
//
//     // Reuse Shopify's own rendered country-selector form (same one the
//     // shopper would use manually) so the POST target/fields/CSRF are exactly
//     // what Shopify expects — desktop form is id="HeaderCountryForm", mobile
//     // drawer is id="HeaderCountryMobileForm".
//     var form = document.querySelector('form[id$="CountryForm"]');
//     var input = form && form.querySelector('input[name="country_code"]');
//     if (!form || !input) return false; // selector not on this page — leave market as-is
//
//     input.value = targetCountry;
//     form.submit();
//     return true;
//   }
//
//   // ---- Exchange rate (INR -> target) ----------------------------------------
//   // Returns a Promise<number|null>. null means "no usable rate" — the caller
//   // falls back to showing the store's own INR price rather than guessing.
//   function getRate(target) {
//     if (target === 'INR') return Promise.resolve(1);
//
//     return fetch('https://open.er-api.com/v6/latest/INR')
//       .then(function (r) { return r.json(); })
//       .then(function (d) {
//         if (!d || !d.rates) throw new Error('rate lookup failed');
//         return typeof d.rates[target] === 'number' ? d.rates[target] : null;
//       })
//       .catch(function () {
//         return null;
//       });
//   }
//
//   // ---- Price rewriting ------------------------------------------------------
//   // Match INR money tokens like: ₹16,600.00 / Rs. 16,600 / ₹16,600.00 INR
//   var INR_MONEY_RE = /(?:₹|Rs\.?|INR)\s?(\d[\d,]*(?:\.\d{1,2})?)(?:\s?INR)?/gi;
//
//   function makeFormatter(currency) {
//     try {
//       return new Intl.NumberFormat(undefined, {
//         style: 'currency',
//         currency: currency,
//         minimumFractionDigits: CONFIG.fractionDigits,
//         maximumFractionDigits: CONFIG.fractionDigits
//       });
//     } catch (e) {
//       return null;
//     }
//   }
//
//   // Each text node's pristine INR text (as rendered by the server) is cached
//   // the first time we see it, so conversion is always derived from that
//   // pristine text rather than any previous conversion's output — exact, never
//   // compounding rounding error.
//   var originalText = new WeakMap();
//
//   function convertTextNode(node, rate, formatter) {
//     if (!originalText.has(node)) {
//       originalText.set(node, node.nodeValue);
//     }
//     var original = originalText.get(node);
//     if (original.indexOf('₹') === -1 && !/Rs|INR/i.test(original)) return;
//
//     if (rate === 1 && !formatter) {
//       if (node.nodeValue !== original) node.nodeValue = original;
//       return;
//     }
//
//     var replaced = original.replace(INR_MONEY_RE, function (_match, num) {
//       var amount = parseFloat(num.replace(/,/g, ''));
//       if (isNaN(amount)) return _match;
//       var converted = amount * rate;
//       return formatter ? formatter.format(converted) : String(Math.round(converted));
//     });
//     if (node.nodeValue !== replaced) node.nodeValue = replaced;
//   }
//
//   function walkAndConvert(root, rate, formatter) {
//     var containers = [];
//     CONFIG.priceSelectors.forEach(function (sel) {
//       root.querySelectorAll(sel).forEach(function (el) { containers.push(el); });
//     });
//     if (root.matches) {
//       CONFIG.priceSelectors.forEach(function (sel) {
//         if (root.matches(sel)) containers.push(root);
//       });
//     }
//     containers.forEach(function (container) {
//       var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
//       var textNodes = [];
//       var n;
//       while ((n = walker.nextNode())) textNodes.push(n);
//       textNodes.forEach(function (tn) { convertTextNode(tn, rate, formatter); });
//     });
//   }
//
//   // ---- Active currency state --------------------------------------------------
//   var state = { code: CONFIG.baseCurrency, rate: 1, formatter: null, detectedCountry: null };
//
//   function applyCurrency(code) {
//     return getRate(code).then(function (rate) {
//       var formatter = code === CONFIG.baseCurrency ? null : makeFormatter(code);
//       if (rate === null || (code !== CONFIG.baseCurrency && !formatter)) {
//         // No usable rate/formatter — show the store's own INR price rather
//         // than a broken/guessed conversion.
//         state = { code: CONFIG.baseCurrency, rate: 1, formatter: null, detectedCountry: state.detectedCountry };
//       } else {
//         state = { code: code, rate: rate, formatter: formatter, detectedCountry: state.detectedCountry };
//       }
//       walkAndConvert(document.body, state.rate, state.formatter);
//       return state.code;
//     });
//   }
//
//   // Re-convert dynamically injected/updated prices (cart drawer, variant
//   // change, quick-add, pagination, etc.) using whichever currency is
//   // currently active. Debounced to avoid thrash.
//   function observeDynamicContent() {
//     var pending = false;
//     var observer = new MutationObserver(function (mutations) {
//       if (pending) return;
//       pending = true;
//       window.requestAnimationFrame(function () {
//         pending = false;
//         mutations.forEach(function (m) {
//           m.addedNodes.forEach(function (node) {
//             if (node.nodeType === 1) walkAndConvert(node, state.rate, state.formatter);
//           });
//           if (m.type === 'characterData' && m.target && m.target.parentNode) {
//             walkAndConvert(m.target.parentNode, state.rate, state.formatter);
//           }
//         });
//       });
//     });
//     observer.observe(document.body, { childList: true, subtree: true, characterData: true });
//   }
//
//   // ---- Debug helper (read-only) ----------------------------------------------
//   // `LeyaGeo.info.detectedCountry` shows exactly what the geo-IP API returned
//   // for this page load, so a wrong currency can be diagnosed as either "the
//   // geo-IP API misreported the country" (detectedCountry is wrong) or "our
//   // country -> currency mapping is wrong" (detectedCountry is right, code is
//   // wrong) without needing to inspect the network tab.
//   function installHelpers() {
//     window.LeyaGeo = {
//       get info() { return state; }
//     };
//   }
//
//   // ---- Boot -----------------------------------------------------------------
//   function boot() {
//     observeDynamicContent();
//     installHelpers();
//
//     // One-time cleanup of cached keys from earlier versions of this script
//     // (localStorage-cached country, sessionStorage-cached country, cached FX
//     // rates) so nothing lingers now that caching has been removed entirely.
//     try { localStorage.removeItem('leya_geo_country'); } catch (e) {}
//     try { localStorage.removeItem('leya_fx_rates'); } catch (e) {}
//     try { localStorage.removeItem('leya_fx_rates_last'); } catch (e) {}
//     try { sessionStorage.removeItem('leya_geo_country'); } catch (e) {}
//
//     detectCountry().then(function (country) {
//       state.detectedCountry = country;
//       if (syncShopifyMarket(country)) return; // page is reloading onto the correct market
//       applyCurrency(currencyForCountry(country));
//     });
//   }
//
//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', boot);
//   } else {
//     boot();
//   }
// })();
